import React, { createContext, useContext, useEffect, useId, type ReactNode } from "react";
import { usePageVisibility } from "../../hooks/usePageVisibility";

// ---------------------------------------------------------------------------
// SubscriptionScope — FR-032 (v6-E canonical location)
//
// PRD refs: specs/prd.md FR-032, specs/architecture.md §4.6
// Task: T317 (relocates the v5-E component from src/api/components/ to
// src/components/layout/ per the v6-E architecture spec).
//
// The SubscriptionScope wraps all project-level routes. It manages the
// lifecycle of Convex composite subscriptions by providing a boolean context
// value that project-level hooks use to decide whether to pass actual
// arguments or `"skip"` to `useQuery`. This lets the dashboard pause
// expensive subscriptions (like `projectDashboard`) when the user navigates
// away from a project page — without tearing down the component tree.
//
// Usage:
//   <SubscriptionScope active={isOnProjectPage}>
//     <ProjectDashboardProvider projectId={id}>
//       <ProjectScreens />
//     </ProjectDashboardProvider>
//   </SubscriptionScope>
//
// And inside a hook:
//   const active = useSubscriptionActive();
//   const data = useQuery(api.foo.bar, active ? args : "skip");
//
// Design notes:
//   - Default `active` value is `true` so hooks work correctly even if
//     accidentally rendered outside a provider. This is a safe fallback
//     (wastes bandwidth but does not break functionality). See ADR-003
//     referenced in specs/architecture.md §4.6.
//   - A secondary `InsideScopeCtx` lets callers (notably
//     `ProjectDashboardProvider`) distinguish "outside any scope" from
//     "inside an active scope". It is used to emit a development-mode
//     warning when the project dashboard mounts without a scope wrapper.
//   - The legacy module `src/api/components/SubscriptionScope.tsx`
//     re-exports from this file, so every existing import keeps resolving
//     to the same React context instance and behaviour is preserved.
// ---------------------------------------------------------------------------

/** Context carrying the boolean `active` flag. Default `true`. */
const SubscriptionActiveCtx = createContext<boolean>(true);

/**
 * Secondary context carrying a sentinel boolean of whether the consumer
 * is rendered inside a `<SubscriptionScope>` wrapper at all. Default is
 * `false` — the provider sets it to `true`. This is intentionally
 * separate from `SubscriptionActiveCtx` so consumers can distinguish
 * "outside any scope" from "inside an inactive scope".
 */
const InsideScopeCtx = createContext<boolean>(false);

export interface SubscriptionScopeProps {
  /** Whether subscriptions within this scope should be active. */
  active: boolean;
  /**
   * When true, the scope additionally pauses subscriptions while the
   * document is hidden (browser tab in background). Effective `active`
   * value seen by descendants becomes `active && documentIsVisible`.
   *
   * Default `false` — preserves existing behaviour (shell-level scopes
   * keep their subscriptions active across visibility changes). Workspace
   * and entity-level scopes opt in by passing `pauseOnHidden`.
   *
   * Source contract: specs/prd.md FR-007, NFR-011 (v.1D §7.2).
   */
  pauseOnHidden?: boolean;
  children: ReactNode;
}

/**
 * Provider that controls whether descendant queries should subscribe.
 *
 * When `active` is `false`, descendant hooks that follow the
 * `useSubscriptionActive()` pattern will pass `"skip"` to `useQuery`,
 * pausing the Convex subscription without unmounting the React tree.
 *
 * When `pauseOnHidden` is `true`, the scope additionally pauses while the
 * tab is in the background — `useSubscriptionActive()` returns `false`
 * whenever `document.visibilityState === "hidden"`, regardless of the
 * `active` prop value. When the tab becomes visible again the scope
 * reactivates automatically.
 */
export function SubscriptionScope({
  active,
  pauseOnHidden = false,
  children,
}: SubscriptionScopeProps) {
  // NFR-005: Subscription-scope teardown measurement (Section 16.3).
  // Uses performance.mark/measure so Playwright can read via page.evaluate.
  // React's intrinsic `key` is not accessible inside the component, so
  // useId() provides a stable per-instance identifier for the marks.
  const scopeId = useId();
  useEffect(() => {
    const startMark = `subscription_scope_teardown_start_${scopeId}`;
    performance.mark(startMark);
    return () => {
      const endMark = `subscription_scope_teardown_end_${scopeId}`;
      performance.mark(endMark);
      performance.measure("subscription_scope_teardown", startMark, endMark);
    };
  }, [scopeId]);

  // FR-007 / NFR-011 — visibility-gated subscription burst reduction.
  // Hook is always called (Rules-of-Hooks) but the result is only folded
  // into the effective `active` value when pauseOnHidden is true.
  const pageVisible = usePageVisibility();
  const effectiveActive = active && (!pauseOnHidden || pageVisible);

  return (
    <InsideScopeCtx.Provider value={true}>
      <SubscriptionActiveCtx.Provider value={effectiveActive}>
        {children}
      </SubscriptionActiveCtx.Provider>
    </InsideScopeCtx.Provider>
  );
}

/**
 * Returns whether the nearest `SubscriptionScope` is active.
 *
 * Defaults to `true` when rendered outside any provider so hooks work
 * correctly in isolated unit tests and ad-hoc trees. See the module
 * header for the full rationale.
 *
 * Usage:
 *   const active = useSubscriptionActive();
 *   const data = useQuery(api.foo.bar, active ? args : "skip");
 */
export function useSubscriptionActive(): boolean {
  return useContext(SubscriptionActiveCtx);
}

/**
 * Returns `true` only when the caller is rendered inside a
 * `<SubscriptionScope>` wrapper. Used by `ProjectDashboardProvider` to
 * emit a development-mode warning when mounted outside any scope —
 * which usually indicates a missing `<SubscriptionScope>` wrapper
 * around the project routes.
 *
 * This is NOT the same as `useSubscriptionActive()`, which returns `true`
 * when outside a scope (the safe fallback behaviour).
 */
export function useIsInsideSubscriptionScope(): boolean {
  return useContext(InsideScopeCtx);
}

export default SubscriptionScope;
